import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import {withStyles} from '@mui/styles';
import {
  createReceipt,
  deleteReceipt,
  generateReport,
  getReceipts, lockReceipt, unlockReceipt,
  updateReceipt
} from "../../utils/api";
import {Chip, CircularProgress, TableSortLabel, TextField, Tooltip, tooltipClasses} from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {useInView} from "react-cool-inview";
import debounce from "lodash.debounce";
import AddReceiptDialog from "../dialogs/AddReceiptDialog";
import {UserContext, isReadOnlyRole} from "../auth/UserProvider";
import {formatTime} from "../../utils/timeHelper";
import ReceiptDetailViewDialog from "../dialogs/ReceiptDetailViewDialog";
import DownloadBudgetDialog from "../dialogs/DownloadBudgetDialog";
import * as XLSX from "xlsx-js-style";
import DownloadReceiptList from "../dialogs/DownloadReceiptListDialog";
import Box from "@mui/material/Box";
import ConfirmActionDialog from "../dialogs/ConfirmActionDialog";
import {AttachmentOutlined, LockOutlined} from "@mui/icons-material";
import styled from "styled-components";

const styles = () => ({
  paper: {
    maxWidth: '80%',
    minWidth: '70%',
    margin: 'auto',
    marginTop: '2rem',
    overflow: 'hidden',
  },
  toolbar: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  block: {
    display: 'block',
  },
  contentWrapper: {
    margin: '16px 16px',
  },
  fab: {
    position: 'absolute',
    bottom: '2rem',
    right: '2rem',
  },
});

function Accounting(props) {
  const {classes} = props;
  const [items, setItems] = useState([]);
  const pageToLoad = useRef(0);
  const initialPageLoaded = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('transaction_date');
  const [searchTerm, setSearchTerm] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const didMount = useRef(false);

  const authState = useContext(UserContext);

  const HtmlTooltip = styled(({ className, children, ...props }) => (
      <Tooltip {...props} classes={{ popper: className }}>
        {children}
      </Tooltip>
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }));

  const {observe} = useInView({
    // For better UX, we can grow the root margin so the data will be loaded earlier
    rootMargin: "50px 0px",
    // When the last item comes to the viewport
    onEnter: ({unobserve, observe}) => {
      if (hasMore) {
        // Pause observe when loading data
        unobserve();
        // Load more data
        loadItems(false).then(observe());

        initialPageLoaded.current = true;
        pageToLoad.current = Number(pageToLoad.current) + 1;
      }

    },
  });

  const debouncedSetSearchTerm = useCallback(debounce(term => setSearchTerm(term), 500), []);

  const handleSearchTermChanged = (event) => {
    debouncedSetSearchTerm(event.target.value);
  };

  const removeReceipt = async (receiptId, deleteType = 'SOFT') => {
    await deleteReceipt(receiptId, deleteType);
    await loadItems(true);
  };

  const setReceiptLocked = async (receiptId) => {
    await lockReceipt(receiptId);
    await loadItems(true);
  };

  const setReceiptUnlocked = async (receiptId) => {
    await unlockReceipt(receiptId);
    await loadItems(true);
  };

  const loadItems = async (reset = false) => {
    const partnerId = '';
    const data = await getReceipts(partnerId, searchTerm, orderBy, order, reset ? 0 : pageToLoad.current);
    setHasMore(data.data.totalPages > pageToLoad.current);
    setItems(reset ? data.data.content : prevItems => [...prevItems, ...data.data.content].reduce((unique, o) => {
      if (!unique.some(obj => obj.id === o.id)) {
        unique.push(o);
      }
      return unique;
    }, []));

    if (reset) {
      pageToLoad.current = 0;
    }
  };

  function getTransactionRowsFromData(data) {
    let rows = [];

    // Sort data.items by transaction date ascending and iterate each item
    data.receipts.sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate)).forEach(item => {
      rows.push(getTransactionRowFromData(item));
    });

    return rows;
  }

  function getTransactionRowFromData(item) {
    let budgetLineCode = '';
    // Get the string before the last occurence of .
    if (item.budgetLine) {
      budgetLineCode = item.budgetLine.trim().split(/\s+/)[0];
    }

    let accountingPeriod = '';
    // Get the year and month from item.transactionDate
    if (item.transactionDate) {
      const date = new Date(item.transactionDate);
      accountingPeriod = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(3, '0')}`;
    }

    return [{
      v: formatTime(item.transactionDate, 'dd/LL/yyyy'),
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: accountingPeriod,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.transactionRef,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.description,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.activity ? item.activity.name : '',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.amount,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.intermediateExchangeRate > 0 ? item.intermediateCurrency : item.currency,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.intermediateExchangeRate ? item.intermediateExchangeRate : 1,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.amount > 0 && item.intermediateExchangeRate > 0 ? calculateConvertedAmount(item.amount, item.intermediateExchangeRate) : item.amount,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.currency,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.exchangeRate,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.amount > 0 && item.exchangeRate > 0 ? calculateReportingAmount(item.amount, item.intermediateExchangeRate, item.exchangeRate) : 0,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: 'EUR',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: budgetLineCode,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.budgetLine,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.organisation ? item.organisation.name : '',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.attachments && item.attachments.length > 0 ? 'Yes' : 'No',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: item.locked ? 'Yes' : 'No',
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }];
  }


  function getBudgetLineRowFromData(data, budgetLine) {
    // Find budgetLine items in data.items
    const budgetLineItems = data.items.filter(item => item.budgetLine === budgetLine);

    let bfdwValue = 0;
    let afvValue = 0;
    let yfiValue = 0;
    let apiValue = 0;
    let rebootValue = 0;
    let icscValue = 0;

    budgetLineItems.forEach(item => {
      switch (item.organisation.id) {
        case 3:
          bfdwValue = (Math.round(item.totalAmount * 100) / 100).toFixed(2);
          break;
        case 1:
          afvValue = (Math.round(item.totalAmount * 100) / 100).toFixed(2);
          break;
        case 5:
          yfiValue = (Math.round(item.totalAmount * 100) / 100).toFixed(2);
          break;
        case 2:
          apiValue = (Math.round(item.totalAmount * 100) / 100).toFixed(2);
          break;
        case 4:
          rebootValue = (Math.round(item.totalAmount * 100) / 100).toFixed(2);
          break;
        case 6:
          icscValue = (Math.round(item.totalAmount * 100) / 100).toFixed(2);
          break;
      }
    });

    const totalValue = (Math.round((parseFloat(bfdwValue) + parseFloat(afvValue) + parseFloat(yfiValue) + parseFloat(apiValue) + parseFloat(rebootValue) + parseFloat(icscValue)) * 100) / 100).toFixed(2);

    return [{
      v: budgetLine,
      s: {
        font: {sz: 10, name: "Arial"},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: '',
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: '',
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: totalValue,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: bfdwValue,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        fill: {fgColor: {rgb: "E2EFDA"}},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: afvValue,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        fill: {fgColor: {rgb: "EDEDED"}},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: yfiValue,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        fill: {fgColor: {rgb: "FCE4D6"}},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: apiValue,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        fill: {fgColor: {rgb: "FFFFCC"}},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: rebootValue,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        fill: {fgColor: {rgb: "D6DCE4"}},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }, {
      v: icscValue,
      t: 'n',
      z: '#,##0.00',
      s: {
        font: {sz: 10, name: "Arial"},
        alignment: {horizontal: 'right'},
        fill: {fgColor: {rgb: "FFF2CC"}},
        border: {
          top: {style: "thin", color: {rgb: "000000"}},
          left: {style: "thin", color: {rgb: "000000"}},
          right: {style: "thin", color: {rgb: "000000"}},
          bottom: {style: "thin", color: {rgb: "000000"}}
        }
      }
    }];
  }

  const downloadReceiptList = (data) => {
    let wb = XLSX.utils.book_new();
    wb.SheetNames.push('Transaction list');

    let ws_data = [
      [''],
      [{
        v: 'Transaction list', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: {horizontal: 'center', vertical: 'center'}
        }
      }],
      [''],
      [{
        v: 'Contract number:', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: {}
        }
      }],
      [''],
      [{
        v: 'Project title:', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: {}
        }
      }],
      [''],
      [{
        v: 'Project partner:', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: {}
        }
      }],
      [''],
      [{
        v: 'Project period:', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: {}
        }
      }],
      [''],
      [{
        v: 'Transaction date', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Accounting period', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Transaction ref', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Description', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Output activity', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Original amount', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Original currency', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Exchange Rate', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Functional amount', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Functional currency', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Exchange Rate', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Reporting amount', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Reporting currency', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Donor budget line code', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Donor budget line description', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Partner', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Attachments', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      },{
        v: 'Locked', s: {
          font: {sz: 12, bold: true, name: "Arial"},
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      }],
    ];

    ws_data.push(...getTransactionRowsFromData(data));

    wb.Sheets['Transaction list'] = XLSX.utils.aoa_to_sheet(
        ws_data);

    let budgetSheet = wb.Sheets['Transaction list'];
    /* create !cols array if it does not exist */
    if (!budgetSheet["!cols"]) budgetSheet["!cols"] = [];

    /* write a loop that does the previous two steps for all 14 columns */
    for (let i = 1; i < 14; i++) {
      if (!budgetSheet["!cols"][i]) budgetSheet["!cols"][i] = {wch: 8};
      /* set column width */
      budgetSheet["!cols"][i].wpx = 100;
    }
    if (!budgetSheet["!cols"][14]) budgetSheet["!cols"][14] = {wch: 8};
    /* set column width */
    budgetSheet["!cols"][4].wpx = 200;
    budgetSheet["!cols"][14].wpx = 350;

    budgetSheet["!merges"] = [
      {s: {r: 1, c: 0}, e: {r: 1, c: 9}},
    ];

    XLSX.writeFile(wb,
        `Transaction_List_${formatTime(data.fromDate, 'yyyy-LL-dd')}-${formatTime(data.toDate, 'yyyy-LL-dd')}.xlsx`);

  }

  const downloadBudgetReport = (data) => {
    let wb = XLSX.utils.book_new();
    wb.SheetNames.push('Budget Report');

    let ws_data = [
      [{v: 'Budget Report', s: {font: {sz: 12, bold: true, name: "Arial"}}}],
      [`From: ${data.fromDate}`],
      [`To: ${data.toDate}`],
      ['', {v: 'Contract No NDICI ASIA/2022/438-618', s: {font: {sz: 10, bold: true, name: "Arial"}}}],
      ['', {
        v: 'Implementation period of the contract (01/05/2023-30/04/2027)',
        s: {font: {sz: 10, bold: true, name: "Arial"}}
      }],
      [''],
      ['', {
        v: 'Expenditure incurred',
        s: {
          font: {sz: 12, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "C0C0C0"}},
          alignment: {horizontal: 'center', vertical: 'center'}
        }
      }, '', '', {
        v: 'BfdW',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "E2EFDA"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "medium", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'Vietnam',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "EDEDED"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "medium", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'Indonesia',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "FCE4D6"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "medium", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'Cambodia',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "FFFFCC"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "medium", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'Philippines',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "D6DCE4"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "medium", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'Regional',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "FFF2CC"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "medium", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "thin", color: {rgb: "000000"}}
          }
        }
      }],
      ['', {
        v: '# Units',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          fill: {fgColor: {rgb: "C0C0C0"}}
        }
      }, {
        v: 'Unit value',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "C0C0C0"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true}
        }
      }, {
        v: 'Total Cost (in EUR)',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          fill: {fgColor: {rgb: "C0C0C0"}}
        }
      }, {
        v: 'Costs (in EUR)', s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "E2EFDA"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "medium", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'AFV',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "EDEDED"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "medium", color: {rgb: "000000"}}
          }
        }
      },
        {
          v: 'YFI',
          s: {
            font: {sz: 10, bold: true, name: "Arial"},
            fill: {fgColor: {rgb: "FCE4D6"}},
            alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
            border: {
              top: {style: "thin", color: {rgb: "000000"}},
              left: {style: "thin", color: {rgb: "000000"}},
              right: {style: "thin", color: {rgb: "000000"}},
              bottom: {style: "medium", color: {rgb: "000000"}}
            }
          }
        }, {
        v: 'API',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "FFFFCC"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "medium", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'Reboot', s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "D6DCE4"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "medium", color: {rgb: "000000"}}
          }
        }
      }, {
        v: 'ICSC',
        s: {
          font: {sz: 10, bold: true, name: "Arial"},
          fill: {fgColor: {rgb: "FFF2CC"}},
          alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
          border: {
            top: {style: "thin", color: {rgb: "000000"}},
            left: {style: "thin", color: {rgb: "000000"}},
            right: {style: "thin", color: {rgb: "000000"}},
            bottom: {style: "medium", color: {rgb: "000000"}}
          }
        }
      }],
      [
        {v: '1. Human Resources', s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}}
      ],
      [
        {v: '1.1 Salaries (gross salaries including social security charges and other related costs, local staff)', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      [
        {v: '1.1.1 Technical', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      getBudgetLineRowFromData(data, '1.1.1.1 Project Director AFV 14%'),
      getBudgetLineRowFromData(data, '1.1.1.2 Programme Coordination Manager AFV 28%'),
      getBudgetLineRowFromData(data, '1.1.1.3 Project Operations Coordinator AFV 17%'),
      getBudgetLineRowFromData(data, '1.1.1.4 Project Officer AFV 33%'),
      getBudgetLineRowFromData(data, '1.1.1.5 MEAL Officer - AFV'),
      getBudgetLineRowFromData(data, '1.1.1.6 Communication & Visibility Officer AFV 36%'),
      getBudgetLineRowFromData(data, '1.1.1.7 Project manager & oversight ICSC 30%'),
      getBudgetLineRowFromData(data, '1.1.1.8 Project Coordinator (Overall) ICSC 80%'),
      getBudgetLineRowFromData(data, '1.1.1.9 Climate Policy Officer ICSC 60%'),
      getBudgetLineRowFromData(data, '1.1.1.11 Comms officer content ICSC 50%'),
      getBudgetLineRowFromData(data, '1.1.1.12 Comms officer digital ICSC 50%'),
      getBudgetLineRowFromData(data, '1.1.1.13 MEAL officer ICSC 50%'),
      getBudgetLineRowFromData(data, '1.1.1.14 Project Coordinator (Communication) ICSC 50%'),
      getBudgetLineRowFromData(data, '1.1.1.15 Senior Climate Policy Officer ICSC 60%'),
      getBudgetLineRowFromData(data, '1.1.1.16 Senior Climate Policy Officer ICSC 60%'),
      [
        {v: '1.1.2 Administrative/ support staff', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      getBudgetLineRowFromData(data, '1.1.2.1 Finance Coordinator - AFV 31%'),
      getBudgetLineRowFromData(data, '1.1.2.2 Finance Officer AFV 23%'),
      getBudgetLineRowFromData(data, '1.1.2.3 Project Finance Support Officer AFV 50%'),
      getBudgetLineRowFromData(data, '1.1.2.4 Admin and Logistics Officer AFV 39%'),
      getBudgetLineRowFromData(data, '1.1.2.5 Project Assistant BFDW 100%'),
      getBudgetLineRowFromData(data, '1.1.2.6 Admin and Logistics Officer ICSC 50%'),
      getBudgetLineRowFromData(data, '1.1.2.7 Finance officer ICSC 80%'),
      getBudgetLineRowFromData(data, '1.1.2.8 Project assistant ICSC 50%'),
      [
        {v: '1.2 Salaries (gross salaries including social security charges and other related costs, expat/int. staff)', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      getBudgetLineRowFromData(data, '1.2.1 Project Coordinator BFDW 100%'),
      [
        {v: '1.3 Per diems for missions/travel', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      [
        {v: '   1.3.1 Abroad (staff assigned to the Action)', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      getBudgetLineRowFromData(data, '1.3.1.1 Abroad (Europe - Vietnam) - BFDW'),
      getBudgetLineRowFromData(data, '1.3.1.2 Abroad (Europe - Cambodia) - BFDW'),
      getBudgetLineRowFromData(data, '1.3.1.3 Abroad (Europe - Indonesia) - BFDW'),
      getBudgetLineRowFromData(data, '1.3.1.4 Abroad (Europe - Philippines) - BFDW'),
      getBudgetLineRowFromData(data, '1.3.1.5 Abroad (Vietnam/Philippines - Cambodia) - BFDW & ICSC'),
      getBudgetLineRowFromData(data, '1.3.1.6 Abroad (Vietnam/Philippines - Indonesia) - BFDW & ICSC'),
      getBudgetLineRowFromData(data, '1.3.1.7 Abroad (Vietnam - Philippines) - BFDW & ICSC'),
      [
        {v: '   1.3.2 Local (staff assigned to the Action)', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      getBudgetLineRowFromData(data, '1.3.2.1 National (Across cities/provinces in a country) - AFV'),
      getBudgetLineRowFromData(data, '1.3.2.2 National (Across cities/provinces in a country) - BFDW'),
      getBudgetLineRowFromData(data, '1.3.2.3 Local (for stake holders travel within a city/province)'),
      [
        {v: '   1.3.3 Seminar/conference participants', s: {font: {sz: 10, bold: true, name: "Arial"}}}
      ],
      getBudgetLineRowFromData(data, '1.3.3.1 Perdiem for 1 exposure visit from ASEAN Member States to European Union Member States'),
      getBudgetLineRowFromData(data, '1.3.3.2 Perdiem for activities within Vietnam - Cambodia - Indonesia - the Philippines'),
      [{
        v: 'Subtotal Human Resources',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      },
        {
          f: 'SUM(D12:D53)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
        f: 'SUM(E12:E53)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(F12:F53)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(G12:G53)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(H12:H53)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }, {
        f: 'SUM(I12:I53)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }, {
        f: 'SUM(J12:J53)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }],
      [{v: '2. Travel', s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}}],
      [{v: '2.1 International travel (staff assigned to the Action)', s: {font: {sz: 10, bold: true, name: "Arial"}}}],
      getBudgetLineRowFromData(data, '2.1.1 Abroad (Europe - ASEAN) for kick-off workshop and closing workshop'),
      getBudgetLineRowFromData(data, '2.1.2 Abroad (Vietnam - Cambodia - Indonesia - the Philipines) for monitoring trips'),
      getBudgetLineRowFromData(data, '2.1.3 Abroad (Europe - ASEAN) for 1 exposure visit from ASEAN Member States to European Union Member States'),
      getBudgetLineRowFromData(data, '2.1.4 Abroad (Vietnam - Cambodia - Indonesia - the Philippines) For Activities'),
      [{v: '2.2 Local transportation (staff assigned to the Action)', s: {font: {sz: 10, bold: true, name: "Arial"}}}],
      getBudgetLineRowFromData(data, '2.2.1 National travel (Vietnam)'),
      getBudgetLineRowFromData(data, '2.2.2 Local transportation for local stakeholders'),
      getBudgetLineRowFromData(data, '2.2.3 Travel for Seminar/conference participants'),
      [
          {v: 'Subtotal Travel', s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}}, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      },
        {
          f: 'SUM(D57:D64)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
        f: 'SUM(E57:E64)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(F57:F64)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(G57:G64)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(H57:H64)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }, {
        f: 'SUM(I57:I64)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }, {
        f: 'SUM(J57:J64)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }],
      [{
        v: '3. Equipment and supplies',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }],
      getBudgetLineRowFromData(data, '3.2.1 Working station'),
      getBudgetLineRowFromData(data, '3.2.2 Laptops'),
      getBudgetLineRowFromData(data, '3.2.3 Printers'),
      getBudgetLineRowFromData(data, '3.2.4 Computer Screen'),
      [{
        v: 'Subtotal Equipment and supplies',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      },
      {
        f: 'SUM(D67:D70)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(E67:E70)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(F67:F70)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(G67:G70)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(H67:H70)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }, {
        f: 'SUM(I67:I70)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }, {
        f: 'SUM(J67:J70)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name:"Arial"}, fill:{fgColor:{rgb:"C0C0C0"}}}
      }],
      [{v: '4. Local office', s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}}],
      getBudgetLineRowFromData(data, '4.1.1 Vietnam AFV (20%)'),
      getBudgetLineRowFromData(data, '4.1.2 Philippines ICSC (16,4%)'),
      getBudgetLineRowFromData(data, '4.2 Consumables - office supplies'),
      getBudgetLineRowFromData(data, '4.3.1 Office other services (20%)'),
      getBudgetLineRowFromData(data, '4.3.2 Office maintenance (20%)'),
      [{v: 'Subtotal Local office', s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}}, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(D73:D77)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(E73:E77)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(F73:F77)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(G73:G77)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(H73:H77)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(I73:I77)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(J73:J77)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }],
      [{
        v: '5. Other costs, services',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }],
      getBudgetLineRowFromData(data, '5.2.1 Baseline Survey'),
      getBudgetLineRowFromData(data, '5.2.2 Market Research'),
      getBudgetLineRowFromData(data, '5.3 Expenditure verification'),
      getBudgetLineRowFromData(data, '5.4.1.1 Mid-term Evaluation'),
      getBudgetLineRowFromData(data, '5.4.1.2 Final Evaluation'),
      getBudgetLineRowFromData(data, '5.4.2.1 Develop MnE system'),
      getBudgetLineRowFromData(data, '5.6 Financial services (bank guarantee costs etc.)'),
      getBudgetLineRowFromData(data, '5.7.1 Catering of conferences/seminars'),
      getBudgetLineRowFromData(data, '5.7.2 Venue rental of conferences/seminars'),
      getBudgetLineRowFromData(data, '5.7.3 Facilitation cost'),
      getBudgetLineRowFromData(data, '5.7.4 Group meeting cost'),
      getBudgetLineRowFromData(data, '5.8.1 Digital newsletters'),
      getBudgetLineRowFromData(data, '5.8.2 Digital banners/posters'),
      getBudgetLineRowFromData(data, '5.8.3 Advert for project activities'),
      getBudgetLineRowFromData(data, '5.8.4 Digital radio/television programs/social media campaigns'),
      getBudgetLineRowFromData(data, '5.8.5 Videos'),
      getBudgetLineRowFromData(data, '5.8.6 Communication and visibility items'),
      [{
        v: 'Subtotal Other costs, services',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(D80:D96)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(E80:E96)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(F80:F96)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(G80:G96)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(H80:H96)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(I80:I96)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'SUM(J80:J96)',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }],
      [{v: '6. Other', s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}}],
      getBudgetLineRowFromData(data, '6.1 Develop and adapt multi-linguistic curriculum modules and learning resources for the EEP'),
      getBudgetLineRowFromData(data, '6.2 Develop and adapt 3 multi-linguistic platforms for the EEP'),
      getBudgetLineRowFromData(data, '6.3 Test and launch e-learning platforms for the EEP'),
      getBudgetLineRowFromData(data, '6.4 Support 50 youth-led initiatives'),
      getBudgetLineRowFromData(data, '6.5 Capacity Development in M&E and INTPA rules'),
      getBudgetLineRowFromData(data, '6.6 Maintenance / digital manager'),
      getBudgetLineRowFromData(data, '6.7 Development EEP mobile app'),
      [{v: 'Subtotal Other', s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}},
        {
          v: '',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          v: '',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          f: 'SUM(D99:D105)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          f: 'SUM(E99:E105)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          f: 'SUM(F99:F105)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          f: 'SUM(G99:G105)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          f: 'SUM(H99:H105)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          f: 'SUM(I99:I105)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
          f: 'SUM(J99:J105)',
          t: 'n',
          z: '#,##0.00',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }],
      [{
        v: '7.  Subtotal direct eligible costs of the Action (1-6)',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      },
        {
          v: '',
          s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
        }, {
        v: '',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'D54+D65+D71+D78+D97+D106',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'E54+E65+E71+E78+E97+E106',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'F54+F65+F71+F78+F97+F106',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'G54+G65+G71+G78+G97+G106',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'H54+H65+H71+H78+H97+H106',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'I54+I65+I71+I78+I97+I106',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }, {
        f: 'J54+J65+J71+J78+J97+J106',
        t: 'n',
        z: '#,##0.00',
        s: {font: {sz: 10, bold: true, name: "Arial"}, fill: {fgColor: {rgb: "C0C0C0"}}}
      }]
    ];

    wb.Sheets['Budget Report'] = XLSX.utils.aoa_to_sheet(
        ws_data);

    let budgetSheet = wb.Sheets['Budget Report'];
    /* create !cols array if it does not exist */
    if (!budgetSheet["!cols"]) budgetSheet["!cols"] = [];

    /* create column metadata object if it does not exist */
    if (!budgetSheet["!cols"][0]) budgetSheet["!cols"][0] = {wch: 8};
    /* set column width */
    budgetSheet["!cols"][0].wpx = 520;

    budgetSheet["!merges"] = [
      {s: {r: 6, c: 1}, e: {r: 6, c: 3}},
    ];

    XLSX.writeFile(wb,
        `Budget_Report_${formatTime(data.fromDate, 'yyyy-LL-dd')}-${formatTime(data.toDate, 'yyyy-LL-dd')}.xlsx`);
  };

  const headCells = [
    { id: 'transaction_date', label: 'Date' },
    { id: 'invoice_number', label: 'Invoice number' },
    { id: 'budget_line', label: 'Budget line' },
  ];

  const generateReportHandler =
      async (from, to, type = 'YEARLY_BUDGET') => {
        try {
          await generateReport({
            'fromDate': from,
            'toDate': to,
            'reportType': type
          }).then((response) => {
            setIsLoading(false);
            if (response.data && (response.data.reportType === 'RECEIPT_LIST')) {
              downloadReceiptList(response.data);
            } else {
              downloadBudgetReport(response.data);
            }
          });
        } catch (error) {
          console.log(error);
        }
      };

  const receiptConfirmationHandler =
      async (organisation,
             invoiceNumber,
             budgetLine,
             transactionDate,
             transactionRefPartner,
             payee,
             description,
             amount,
             currency,
             intermediateCurrency,
             exchangeRate,
             intermediateExchangeRate,
             activity,
             attachments) => {
        const formData = new FormData();

        for (let i = 0; i < attachments.length; i++) {
          formData.append('files', attachments[i]);
        }

        formData.append('receipt', new Blob([JSON.stringify({
          organisation: organisation,
          invoiceNumber: invoiceNumber,
          budgetLine: budgetLine,
          transactionDate: transactionDate,
          transactionRefPartner: transactionRefPartner,
          currency: currency,
          intermediateCurrency: intermediateCurrency,
          payee: payee,
          description: description,
          amount: amount,
          exchangeRate: exchangeRate,
          intermediateExchangeRate: intermediateExchangeRate > 0 ? intermediateExchangeRate : null,
          activity: activity,
        })], {
          type: "application/json"
        }));

        await createReceipt(formData);
        await loadItems(true);
      };

  const receiptUpdateHandler =
      async (receiptId,
             organisation,
             invoiceNumber,
             budgetLine,
             transactionDate,
             transactionRefPartner,
             payee,
             description,
             amount,
             currency,
             intermediateCurrency,
             exchangeRate,
             intermediateExchangeRate,
             activity,
             attachments) => {
        const formData = new FormData();

        for (let i = 0; i < attachments.length; i++) {
          if (attachments[i] instanceof File) {
            formData.append('files', attachments[i]);
          }
        }

        formData.append('receipt', new Blob([JSON.stringify({
          organisation: organisation,
          invoiceNumber: invoiceNumber,
          budgetLine: budgetLine,
          transactionDate: transactionDate,
          transactionRefPartner: transactionRefPartner,
          currency: currency,
          intermediateCurrency: intermediateCurrency,
          payee: payee,
          description: description,
          amount: amount,
          exchangeRate: exchangeRate,
          intermediateExchangeRate: intermediateExchangeRate > 0 ? intermediateExchangeRate : null,
          activity: activity,
          attachments: attachments.filter(file => !(file instanceof File))
        })], {
          type: "application/json"
        }));

        await updateReceipt(receiptId, formData);
        await loadItems(true);
      };

  const sortHandler = (property) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    if (initialPageLoaded.current) {
      return;
    }

    loadItems(false);

    initialPageLoaded.current = true;
    pageToLoad.current = Number(pageToLoad.current) + 1;
  }, [loadItems]);

  useEffect(() => {
    if (didMount.current) {
      loadItems(true);
    } else didMount.current = true;
  }, [searchTerm, orderBy, order]);

  function calculateConvertedAmount(amountToConvert, rate) {
    return Math.round(((amountToConvert / rate) + Number.EPSILON) * 100) / 100;
  }

  // Converts through both the intermediate and reporting exchange rate in one step and
  // rounds once, so the result matches the backend's per-receipt conversion exactly
  // (rounding after each intermediate step compounds cent-level errors).
  function calculateReportingAmount(amount, intermediateExchangeRate, exchangeRate) {
    const rawAmount = intermediateExchangeRate > 0
      ? amount / intermediateExchangeRate / exchangeRate
      : amount / exchangeRate;
    return Math.round((rawAmount + Number.EPSILON) * 100) / 100;
  }

  const renderData = () => {

    if (isLoading || !items) {
      return (
          <div style={{width: '100%', textAlign: 'center', height: '100%'}}>
            <CircularProgress style={{marginTop: '10%'}} color="secondary"/>
          </div>
      );
    }

    return (
        <>

          <div className={classes.contentWrapper} style={{width: '95%'}}>
            <TextField
                label="Organisation, ref, invoice nr., budget line"
                id="activity-filter"
                variant="outlined"
                size="small"
                fullWidth
                onChange={handleSearchTermChanged}
            />
          </div>
          <TableContainer style={{marginLeft: 15, width: '95%'}} component={Paper} className={classes.tableContainer}>
            <Table aria-label="collapsible table">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Organisation</TableCell>
                  {headCells.map((headCell) => (
                      <TableCell key={headCell.id}>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={sortHandler(headCell.id)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      </TableCell>
                  ))}
                  <TableCell>Attachment?</TableCell>
                  <TableCell>Amount (€)</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((receipt, idx) => (
                    <TableRow ref={idx === items.length - 1 ? observe : null} key={idx} className={classes.root}>
                      <TableCell>{receipt.organisation ?
                          <Chip label={receipt.organisation.name} size="small"/> : null}</TableCell>
                      <TableCell>{receipt.transactionDate ? formatTime(receipt.transactionDate) : null}</TableCell>
                      <TableCell>{receipt.invoiceNumber}</TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        {receipt.budgetLine && receipt.budgetLine !== '' ?
                          <Tooltip title={receipt.budgetLine}>
                            <Chip label={receipt.budgetLine} size="small"/>
                          </Tooltip> : null
                        }
                      </TableCell>
                      <TableCell>{receipt.attachments && receipt.attachments.length > 0 ?
                          <HtmlTooltip
                              title={
                                <React.Fragment>
                                  <Typography color="inherit">Attached items ({receipt.attachments.length})</Typography>
                                  { receipt.attachments.map((attachment, idx) => (
                                      <Typography key={idx}>{attachment.filename}</Typography>
                                  ))}
                                </React.Fragment>
                              }
                          >
                            <AttachmentOutlined sx={{ color: 'green', fontSize: 24 }} />
                          </HtmlTooltip> : null}
                      </TableCell>
                      <TableCell
                          sx={{textAlign: "right"}}>{receipt.amount > 0 && receipt.exchangeRate > 0 ? `${calculateReportingAmount(receipt.amount, receipt.intermediateExchangeRate, receipt.exchangeRate)} €` : ''}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          { receipt.locked && authState.role === 'ROLE_SUPER' && authState.org && authState.org.split('_')[1] === '3' &&
                              <ConfirmActionDialog action="unlock" confirmationHandler={() => setReceiptUnlocked(receipt.id)} />
                          }
                          { receipt.locked && authState.org && authState.org.split('_')[1] !== '3' &&
                              <Tooltip style={{ margin: 8 }} title="Receipt is locked! Editing is no longer possible!">
                                <LockOutlined sx={{ fontSize: 24, color: 'green' }} className={classes.block} color="inherit" />
                              </Tooltip>
                          }
                          { !receipt.locked && !isReadOnlyRole(authState.role) &&
                              <ConfirmActionDialog action="lock" confirmationHandler={() => setReceiptLocked(receipt.id)} />
                          }
                          <ReceiptDetailViewDialog receipt={receipt}/>
                          { !receipt.locked && !isReadOnlyRole(authState.role) &&
                            <AddReceiptDialog entity={receipt} action="edit" classes={classes} confirmationHandler={receiptUpdateHandler}/>
                          }
                          { !isReadOnlyRole(authState.role) && (!receipt.locked || (authState.role === 'ROLE_SUPER' && authState.org && authState.org.split('_')[1] === '3')) &&
                            <>
                              <ConfirmActionDialog action="delete" confirmationHandler={() => removeReceipt(receipt.id)} />
                            </>
                          }
                        </Box>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(authState.role === 'ROLE_SUPER' || authState.role === 'ROLE_ANALYST') &&
              <DownloadReceiptList classes={classes} confirmationHandler={generateReportHandler}/>
          }
          {(authState.role === 'ROLE_SUPER' || authState.role === 'ROLE_ANALYST') &&
              <DownloadBudgetDialog classes={classes} confirmationHandler={generateReportHandler}/>
          }
          {!isReadOnlyRole(authState.role) &&
              <AddReceiptDialog classes={classes} confirmationHandler={receiptConfirmationHandler}/>
          }
        </>
    );
  };

  return renderData();
}

Accounting.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Accounting);
